/**
 * Whether this page is the Google Play app or the website.
 *
 * One question, asked for one reason: nothing is for sale inside the Play app.
 * A Play-distributed app that sells a subscription is bound by Play's payments
 * policy, and the fee on top of Stripe's makes a $5 subscription cost more than
 * it earns (docs/play-listing.md, section 0). So the app packaged for Play is a
 * consumption-only client -- everything except the purchase buttons -- and this
 * file is how it knows which it is.
 *
 * Two signals, because neither survives on its own:
 *
 * 1. `document.referrer`, which Chrome sets to `android-app://<package>` on a
 *    Trusted Web Activity's first navigation. Exact package only. A generic
 *    `android-app://` test would also match somebody who tapped a link in Gmail
 *    -- referrer `android-app://com.google.android.gm` -- and hiding the
 *    subscribe button from a person reading the website in Chrome is a bug that
 *    costs money and would never be reported.
 * 2. `?source=play`, which the packaged app's start_url carries. The referrer is
 *    gone after a hard reload; the query arrives again on every launch.
 *
 * Kept in sessionStorage rather than localStorage, which is the one decision
 * here worth arguing about. A TWA runs inside the Chrome profile and shares
 * localStorage with the browser, so a persistent flag written by the Play app
 * would still be there when the same person opens pantry-pro.app in a tab --
 * and they would find an app that refuses to sell them anything, with no way to
 * say why. A per-tab flag is exactly the lifetime of "this window is the app".
 *
 * Every storage and history access is wrapped, the same way referral.js wraps
 * its own: a private window, a browser set to block site data, or an embedded
 * webview throws rather than returning null, and being wrong about the client
 * is not worth a blank screen.
 */

/** Permanent. Also in web/public/.well-known/assetlinks.json and android/twa-manifest.json. */
export const PLAY_PACKAGE = 'app.pantrypro.twa';

const KEY = 'pantry.client';
const PLAY = 'play';
const PLAY_REFERRER = `android-app://${PLAY_PACKAGE}`;

function remember() {
  try {
    window.sessionStorage.setItem(KEY, PLAY);
  } catch { /* no storage: this launch reads as the website, which is the safe way to be wrong */ }
}

/** Read both signals once, at boot, before anything routes. */
export function captureClient() {
  // Two blocks rather than one, because the signals are independent: a browser
  // that throws on document.referrer must not cost us the query string.
  try {
    const referrer = document.referrer || '';
    if (referrer === PLAY_REFERRER || referrer.startsWith(`${PLAY_REFERRER}/`)) remember();
  } catch { /* no referrer to read */ }

  try {
    const url = new URL(window.location.href);
    // Only our own value is consumed. A future ?source=newsletter belongs to
    // whoever added it, and stripping it here would take their attribution
    // away for the sake of a tidier address bar.
    if (url.searchParams.get('source') !== PLAY) return;
    remember();
    url.searchParams.delete('source');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch { /* no history, or no URL to parse: the flag above still stands */ }
}

/** `'play'` or null. Null means the website, which is also every older client. */
export function clientName() {
  try {
    return window.sessionStorage.getItem(KEY) === PLAY ? PLAY : null;
  } catch {
    return null;
  }
}

/** The one thing screens ask. False whenever we cannot tell. */
export function runningInPlayApp() {
  return clientName() === PLAY;
}
